import React, { useState } from "react";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import NaturalNextSteps from "@/components/NaturalNextSteps";
import InterestModal from "@/components/InterestModal";
import TopImage from "@/components/TopImage";
import BodyText from "@/components/BodyText";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/images";

export default function DailyEdge() {
  const [open, setOpen] = useState(false);
  const t = useContent("daily_edge");
  useSeo("daily_edge", {
    title: "Daily Quantum Edge — Quantum Results",
    description:
      "An ongoing practice for those who want to intentionally strengthen the way they think, live, and lead.",
  });
  return (
    <div data-testid="page-daily-edge">
      <TopImage src={IMG.dailyEdge.hero.src} alt={IMG.dailyEdge.hero.alt} credit={IMG.dailyEdge.hero.credit} testId="daily-edge-top-image" />
      <PageHero
        eyebrow={t("hero_eyebrow", "Daily Quantum Edge")}
        title={t("hero_title", <>Some things become part of who we are through <span className="text-brand-blue">daily practice</span>.</>)}
        lede={t("hero_lede", "A place to begin each weekday by reconnecting with yourself, strengthening the way you think, and intentionally preparing for the day ahead.")}
        testId="dqe-hero"
      />

      <Section testId="dqe-intro">
        <BodyText
          className=""
          value={t.body(
            "intro_body",
            `There are some things we don't develop by thinking about them once a week.

Like any meaningful practice, they become stronger through consistent attention.

Daily Quantum Edge was created for people who want more than occasional moments of insight. It's a place to begin each weekday by reconnecting with yourself, strengthening the way you think, and intentionally preparing for the day ahead.

When people describe their experiences with Daily Quantum Edge, one thought comes up again and again.

*"It's the best way to start my day."*

They leave feeling calmer. More grounded. Better prepared to meet whatever the day brings. Often reminded of something they already knew — but needed to remember.

Over time, those daily moments begin shaping something deeper. Not simply what you know. But how you naturally live.`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("section2_eyebrow", "Recognition")}
          title={t("section2_title", "The way we begin our day often shapes everything that follows.")}
          testId="dqe-recognition"
        >
          <BodyText
            className=""
            value={t.body(
              "section2_body",
              `Have you ever noticed how different your day feels when you begin it intentionally?

Not because everything goes according to plan. But because you do.

A few moments of clarity in the morning can influence the way you respond to conversations, decisions, challenges, and opportunities throughout the day.

Without that intentional beginning, it's easy to start reacting before we've had a chance to reconnect with ourselves.

**Daily Quantum Edge creates a simple space to pause before the day gathers momentum. Not to escape your responsibilities. But to meet them with greater clarity, presence, and intention.**`
            )}
          />
        </Section>
      </section>

      <Section
        eyebrow={t("section3_eyebrow", "What you'll experience")}
        title={t("section3_title", "A daily practice. A thoughtful community.")}
        testId="dqe-what"
      >
        <BodyText
          className=""
          value={t.body(
            "section3_body",
            `Each weekday morning, we gather for a simple purpose: to begin the day intentionally.

Together we'll explore practical ideas, meaningful questions, and timeless principles that help us think more clearly, live more intentionally, and meet the day with greater presence.

Some mornings you'll discover a new insight. Other mornings you'll simply be reminded of something you've always known — but needed to hear again.

Over time, something remarkable begins to happen. The conversations become more than daily sessions. They become part of the rhythm of your life.

**And you'll be joined by a community of thoughtful people who are choosing to begin each day in much the same way.**`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("section4_eyebrow", "Is Daily Quantum Edge right for you?")}
          title={t("section4_title", "Daily Quantum Edge may be right for you if…")}
          testId="dqe-fit"
        >
          <BodyText
            className=""
            value={t.body(
              "section4_body",
              `It may be a good fit if you believe the way you begin your day matters.

You value clarity more than urgency.

You appreciate thoughtful conversation, practical ideas, and the opportunity to reconnect with yourself before life becomes busy.

You're not looking for another obligation.

You're looking for a daily rhythm that helps you think more clearly, live more intentionally, and show up as your best self.

**If that describes what you're looking for, I think you'll feel at home here.**`
            )}
          />
        </Section>
      </section>

      <Section
        eyebrow={t("section5_eyebrow", "Begin with a conversation")}
        title={t("section5_title", "Some communities are discovered one conversation at a time.")}
        testId="dqe-begin"
      >
        <BodyText
          className=""
          value={t.body(
            "section5_body",
            `Daily Quantum Edge isn't simply something you join. It's a community of people who have chosen to make intentional living part of everyday life.

Because that shared commitment matters, we begin with a conversation.

We'll get to know one another, explore what you're looking for, and determine whether Daily Quantum Edge feels like the right place for you.

If it does, we'll welcome you into a community that's committed to beginning each day with greater clarity, presence, and intention.

And if it doesn't, I hope our conversation leaves you with greater clarity than when we began.

**Either way, it will have been time well spent.**`
          )}
        />
        <div className="mt-8">
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="dqe-cta"
            className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-brand-cream text-[14px] tracking-wide hover:bg-brand-blue transition-colors"
          >
            Start a Conversation <span aria-hidden>→</span>
          </button>
        </div>
      </Section>

      <InterestModal
        open={open}
        onClose={() => setOpen(false)}
        source="daily-edge"
        title="Daily Quantum Edge"
        lede="Because Daily QE grows through conversation, we begin there. Leave your email and Dan will reach out to talk."
        submitLabel="Send"
      />

      <NaturalNextSteps
        items={[
          { to: "/weekly-quantum-edge", label: "Weekly Quantum Edge — a weekly rhythm" },
          { to: "/four-levels", label: "The Four Levels Experience" },
          { to: "/about", label: "About Dan Hoover" },
        ]}
      />
    </div>
  );
}
